import logoSmall from '../assets/northalley-logo@128.png';
import logoLarge from '../assets/northalley-logo@512.png';

/*
 * Two bundlers, two shapes for the same import.
 *
 * Vite hands back the URL as a string. Next hands back a StaticImageData
 * object and expects `next/image` to unwrap it — so a plain <img> given that
 * object renders "[object Object]", which typechecks under Vite and breaks
 * only in the two portals. Normalising here keeps one component working in
 * both rather than forking the shells.
 */
const url = (mod: unknown): string =>
  typeof mod === 'string' ? mod : ((mod as { src: string }).src ?? '');

const SMALL = url(logoSmall);
const LARGE = url(logoLarge);

export interface BrandMarkProps {
  /** Rendered beside the star. Omit for a mark-only lockup. */
  children?: React.ReactNode;
  /** px. The asset is square, so one number is the whole box. */
  size?: number;
}

/**
 * The NorthAlley star.
 *
 * One component in the design system rather than an <img> in each shell: the
 * three apps must show the SAME mark at the same size, and a copy per app is a
 * copy that drifts the first time one of them is updated on its own.
 *
 * THE ASSET IS THE STAR ALONE, NOT THE SUPPLIED LOCKUP. The brand file is a
 * horizontal lockup — star plus "NorthAlley" wordmark — and this slot is
 * square and as small as 30px in the sidebar. Dropping the lockup in here
 * would squash a 6:1 image into a square, render the wordmark at about 9px,
 * and print the name twice, because every caller already passes the wordmark
 * as text beside it. So the star is cropped out of the lockup and the wordmark
 * stays text: it keeps the accent-coloured OPS / PARTNERS suffix that is the
 * only thing distinguishing the back office from the partner portals.
 *
 * The colour is the brand's own #123938 in LIGHT mode. In dark mode the mark is
 * mapped to white by `.brand-mark` in components.css — #123938 is a dark teal,
 * and on the dark surface it sat at about 1.2:1 against the background and read
 * as a smudge. WCAG exempts logotypes from contrast requirements, so nothing
 * failed; it was simply close to invisible, which is not what a brand mark is
 * for. The recolour lives in CSS rather than in a second asset so there is one
 * file to change and no pair to drift apart.
 *
 * The alt text is empty and aria-hidden is set whenever a label sits beside it.
 * A logo next to the word it spells is decoration, and a screen reader that
 * announces "NorthAlley" twice is reading the same thing twice.
 */
export function BrandMark({ children, size = 28 }: BrandMarkProps) {
  return (
    <>
      {/* Both sizes offered, so the browser picks by device pixel ratio rather
          than upscaling a 128px star on a retina sign-in panel. `sizes` is the
          CSS box; the descriptors are the intrinsic widths. */}
      <img
        src={SMALL}
        srcSet={`${SMALL} 128w, ${LARGE} 512w`}
        sizes={`${size}px`}
        alt={children ? '' : 'NorthAlley'}
        aria-hidden={children ? true : undefined}
        width={size}
        height={size}
        className="brand-mark"
      />
      {children}
    </>
  );
}
